/**
 * F09 v2 — map verification `ApiError`s to admin copy + typed details.
 *
 * The API envelope is `{ error: { code, message, details? } }` (controller
 * ruling for every F09 error, matching `apps/api/src/lib/admin-errors.ts`):
 * extra fields always live under `error.details`, never at the top level —
 * `PREREQUISITE_NOT_MET { failures }`, `DUPLICATE_IDENTITY { matches }`,
 * `MISSING_REQUIRED_DOCUMENTS { missing }`, etc. Details are read from there
 * only; a missing `details` yields `{}`.
 */
import {
  VERIFIABLE_ENTITY_TYPES,
  VERIFICATION_NAME_FIELDS,
  type DuplicateIdentityDetails,
  type MissingRequiredDocumentsDetails,
  type NameChangedDuringReviewDetails,
  type NameLockHolder,
  type NameLockedVerifiedDetails,
  type PrerequisiteNotMetDetails,
  type VerifiableEntityType,
  type VerificationDocumentGroup,
  type VerificationDuplicateMatch,
  type VerificationNameField,
  type VerificationPrerequisiteCode,
} from '@bravemove/types';
import { translateApiError } from '../../auth/errors';
import { ApiError } from '../../auth/types';
import type { Translate } from './labels';

const ADMIN_CODES = new Set([
  'DUPLICATE_IDENTITY',
  'PREREQUISITE_NOT_MET',
  'MISSING_REQUIRED_DOCUMENTS',
  'ID_NUMBER_REQUIRED',
  'CANDIDATES_DISABLED',
  'INVALID_STATUS_TRANSITION',
  'ALREADY_VERIFIED',
  'NOT_VERIFIED',
  'NO_ACTIVE_COOLDOWN',
  'REREQUEST_NOT_GATED',
  'CANDIDATE_INVITE_RATE_LIMITED',
  'VERIFICATION_NOT_FOUND',
  'ENTITY_NOT_FOUND',
  'DOCUMENT_NOT_FOUND',
  'CANDIDATE_NOT_FOUND',
  'WATCHLIST_ENTRY_NOT_FOUND',
  'RATE_LIMITED',
  'VALIDATION_ERROR',
  'NAME_LOCKED_VERIFIED',
]);

export function verificationErrorCode(err: unknown): string | null {
  return err instanceof ApiError ? err.code : null;
}

export function verificationErrorDetails(err: unknown): Readonly<Record<string, unknown>> {
  if (!(err instanceof ApiError) || !err.body?.error) return {};
  const details = (err.body.error as Readonly<Record<string, unknown>>).details;
  return details && typeof details === 'object' && !Array.isArray(details)
    ? (details as Readonly<Record<string, unknown>>)
    : {};
}

// Key names are pinned to the frozen `VerificationErrorDetailsMap` shapes so a
// contract rename fails to compile here instead of silently reading nothing.

export function prerequisiteFailuresOf(err: unknown): readonly VerificationPrerequisiteCode[] {
  const failures =
    verificationErrorDetails(err)['failures' satisfies keyof PrerequisiteNotMetDetails];
  if (!Array.isArray(failures)) return [];
  return failures.filter((f): f is VerificationPrerequisiteCode => typeof f === 'string');
}

export function duplicateMatchesOf(err: unknown): readonly VerificationDuplicateMatch[] {
  const matches = verificationErrorDetails(err)['matches' satisfies keyof DuplicateIdentityDetails];
  return Array.isArray(matches) ? (matches as VerificationDuplicateMatch[]) : [];
}

export function missingGroupsOf(err: unknown): readonly VerificationDocumentGroup[] {
  const missing =
    verificationErrorDetails(err)['missing' satisfies keyof MissingRequiredDocumentsDetails];
  return Array.isArray(missing) ? (missing as VerificationDocumentGroup[]) : [];
}

/** The 422 `NAME_LOCKED_VERIFIED` code (E13) — an admin edit hit a verified name. */
export const NAME_LOCKED_VERIFIED = 'NAME_LOCKED_VERIFIED';

export function isNameLockedError(err: unknown): boolean {
  return verificationErrorCode(err) === NAME_LOCKED_VERIFIED;
}

function isEntityType(v: unknown): v is VerifiableEntityType {
  return typeof v === 'string' && (VERIFIABLE_ENTITY_TYPES as readonly string[]).includes(v);
}

function isNameField(v: unknown): v is VerificationNameField {
  return typeof v === 'string' && (VERIFICATION_NAME_FIELDS as readonly string[]).includes(v);
}

/**
 * The `field` + `lockedBy` holders of a `NAME_LOCKED_VERIFIED` error, or null
 * when `err` is not one. Malformed holder entries are dropped.
 */
export function nameLockDetailsOf(err: unknown): {
  readonly field: VerificationNameField | null;
  readonly lockedBy: readonly NameLockHolder[];
} | null {
  if (!isNameLockedError(err)) return null;
  const d = verificationErrorDetails(err);
  const field = d['field' satisfies keyof NameLockedVerifiedDetails];
  const lockedBy = d['lockedBy' satisfies keyof NameLockedVerifiedDetails];
  const holders: NameLockHolder[] = [];
  if (Array.isArray(lockedBy)) {
    for (const h of lockedBy as unknown[]) {
      if (h && typeof h === 'object') {
        const { entityType, entityId } = h as Record<string, unknown>;
        if (isEntityType(entityType) && typeof entityId === 'string') {
          holders.push({ entityType, entityId });
        }
      }
    }
  }
  return { field: isNameField(field) ? field : null, lockedBy: holders };
}

/** One-line message for a verification error (prerequisite list rendered separately). */
export function verificationErrorMessage(err: unknown, t: Translate): string {
  const code = verificationErrorCode(err);
  if (code === 'NAME_CHANGED_DURING_REVIEW') {
    const d = verificationErrorDetails(err);
    const snapshot = d['snapshotName' satisfies keyof NameChangedDuringReviewDetails];
    const current = d['currentName' satisfies keyof NameChangedDuringReviewDetails];
    return t('admin:verification.errors.NAME_CHANGED_DURING_REVIEW', {
      snapshot: typeof snapshot === 'string' ? snapshot : '—',
      current: typeof current === 'string' ? current : '—',
    });
  }
  if (code && ADMIN_CODES.has(code)) return t(`admin:verification.errors.${code}`);
  return translateApiError(err, t);
}
